/**
 * Labels a www.mtlblog.com page with its frontend and URL band, for the
 * RebelMouse-against-Expo comparison (frontendMeasurementConfig.js). Measurement
 * only: nothing here changes what loads or which ads serve.
 * @module lib/frontendMeasurement
 */

const { FRONTEND_MEASUREMENT: CONFIG } = require('../frontendMeasurementConfig');
const BUNDLE_PATH = require('./vitals/bundlePath');
const { loadScript } = require('./scriptLoader');
const { isAutomatedClient, isTopLevelContext } = require('./identity/context');

let vitalsStarted = false;

function isMeasuredHost(location = typeof window !== 'undefined' ? window.location : null) {
  const host = String(location?.hostname || '').toLowerCase();
  return CONFIG.hosts.includes(host);
}

// The edge Worker's normalizePath and fnv1a32 (mtlblog-app/edge/src/router.js).
function pathBucket(pathname) {
  const trimmed = String(pathname || '').toLowerCase().replace(/\/+$/, '');
  let hash = 0x811c9dc5;
  for (const byte of new TextEncoder().encode(trimmed === '' ? '/' : trimmed)) {
    hash ^= byte;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return (hash >>> 0) % CONFIG.bucketCount;
}

/** The band reports use for arms: '00'–'99'. */
function pathBand(pathname) {
  return String(Math.floor(pathBucket(pathname) / CONFIG.bandSize)).padStart(2, '0');
}

function pageFrontend(doc = typeof document !== 'undefined' ? document : null) {
  const meta = doc && typeof doc.querySelector === 'function'
    ? doc.querySelector(`meta[name="${CONFIG.frontendMetaName}"]`)
    : null;
  const value = String(meta?.getAttribute('content') || '').trim().toLowerCase();
  return CONFIG.frontends.includes(value) ? value : CONFIG.defaultFrontend;
}

/** GAM key-values for this page: `{ fe, pb }` on a measured host, else none. */
function frontendTargeting() {
  if (typeof window === 'undefined' || !isMeasuredHost()) return {};
  return {
    [CONFIG.gamFrontendKey]: pageFrontend(),
    [CONFIG.gamBandKey]: pathBand(window.location.pathname)
  };
}

/**
 * Loads the web-vitals bundle on a sample of measured pages once the page has
 * loaded, so it never competes with the page or its ads.
 */
function setupFrontendVitals({ issuer } = {}) {
  if (vitalsStarted || typeof window === 'undefined' || typeof document === 'undefined') return;
  vitalsStarted = true;
  if (!issuer || !isMeasuredHost() || !isTopLevelContext() || isAutomatedClient()) return;
  if (Math.random() >= CONFIG.vitalsSampleRate) return;
  const start = () => {
    let origin;
    try { origin = new URL(issuer).origin; } catch (_) { return; }
    const labels = { frontend: pageFrontend(), path_band: pathBand(window.location.pathname) };
    loadScript(`${origin}${BUNDLE_PATH}`)
      .then(() => window.MillaVitals.run({ labels }))
      .catch(() => { /* never surface */ });
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}

module.exports = { isMeasuredHost, pathBucket, pathBand, pageFrontend, frontendTargeting, setupFrontendVitals };
